export type AnnotationFrameIdentity = Readonly<{
  camera: string;
  episode: string;
  frameIndex: number;
}>;

function base64UrlJson(value: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/u, "");
}

function opaqueId(namespace: string, value: unknown): string {
  return `${namespace}_${base64UrlJson(value)}`;
}

/** BE `annotation_frame_id` 와 같은 collision-free codec 이다. 생성한 뒤에는 opaque ID 로만 쓴다. */
export function annotationFrameId(camera: string, episode: string, frameIndex: number): string {
  if (!Number.isInteger(frameIndex) || frameIndex < 0) {
    throw new RangeError("frameIndex must be a non-negative integer");
  }
  return opaqueId("frame", [camera, episode, frameIndex]);
}

/**
 * c6 identity 와 registry wire identity 를 연결하는 session-local 양방향 cache.
 * ID 문자열을 나중에 parsing 하지 않는다. 원래 값은 반드시 이 cache 에서 찾는다.
 */
export class AnnotationIdentityRegistry {
  readonly collectionId: string;

  private readonly sequenceByEpisode = new Map<string, string>();
  private readonly episodeBySequence = new Map<string, string>();
  private readonly streamByCamera = new Map<string, string>();
  private readonly cameraByStream = new Map<string, string>();
  private readonly targetByObject = new Map<number, string>();
  private readonly objectByTarget = new Map<string, number>();
  private readonly frameById = new Map<string, AnnotationFrameIdentity>();

  constructor(readonly datasetUuid: string) {
    this.collectionId = opaqueId("collection", datasetUuid);
  }

  sequenceId(episode: string): string {
    const cached = this.sequenceByEpisode.get(episode);
    if (cached !== undefined) return cached;
    const id = opaqueId("sequence", episode);
    this.sequenceByEpisode.set(episode, id);
    this.episodeBySequence.set(id, episode);
    return id;
  }

  episode(sequenceId: string): string | null {
    return this.episodeBySequence.get(sequenceId) ?? null;
  }

  streamId(camera: string): string {
    const cached = this.streamByCamera.get(camera);
    if (cached !== undefined) return cached;
    const id = opaqueId("stream", camera);
    this.streamByCamera.set(camera, id);
    this.cameraByStream.set(id, camera);
    return id;
  }

  camera(streamId: string): string | null {
    return this.cameraByStream.get(streamId) ?? null;
  }

  targetId(objId: number): string {
    if (!Number.isInteger(objId) || objId < 0) {
      throw new RangeError("objId must be a non-negative integer");
    }
    const cached = this.targetByObject.get(objId);
    if (cached !== undefined) return cached;
    const id = opaqueId("target", objId);
    this.targetByObject.set(objId, id);
    this.objectByTarget.set(id, objId);
    return id;
  }

  objId(targetId: string): number | null {
    return this.objectByTarget.get(targetId) ?? null;
  }

  frameId(camera: string, episode: string, frameIndex: number): string {
    const id = annotationFrameId(camera, episode, frameIndex);
    this.bindFrame(id, { camera, episode, frameIndex });
    return id;
  }

  bindFrame(frameId: string, identity: AnnotationFrameIdentity): void {
    if (annotationFrameId(identity.camera, identity.episode, identity.frameIndex) !== frameId) {
      throw new Error("annotation frame ID does not match its locator");
    }
    const existing = this.frameById.get(frameId);
    if (
      existing !== undefined &&
      (existing.camera !== identity.camera ||
        existing.episode !== identity.episode ||
        existing.frameIndex !== identity.frameIndex)
    ) {
      throw new Error("annotation frame ID collision");
    }
    this.sequenceId(identity.episode);
    this.streamId(identity.camera);
    this.frameById.set(frameId, { ...identity });
  }

  frame(frameId: string): AnnotationFrameIdentity | null {
    return this.frameById.get(frameId) ?? null;
  }
}
